import { useState, useEffect, createRef } from 'react';
import * as React from 'react';
import styled from '@emotion/styled';

import { Digit } from './DigitField';

const BACKSPACE_CODE = 8;
const LEFT_ARROW_CODE = 37;
const RIGHT_ARROW_CODE = 39;
const CODE_LENGTH = 6;

interface WrapperProps {
  className?: string;
  justifyContent: string;
  children: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Wrapper = ({ className, justifyContent, children }: WrapperProps): JSX.Element => {
  return <span className={className}>{children}</span>;
};

const StyledWrapper = styled(Wrapper)`
  display: flex;
  width: 100%;
  justify-content: ${(props) => props.justifyContent};

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }
`;

const isValidDigit = (value: string): boolean => {
  const re = /^\d$/;
  return re.test(value);
};

const isValidCode = (value: string): boolean => {
  const re = /^\d{6}$/;
  return re.test(value);
};

const shouldRenderSeparator = (index: number) => {
  return (index + 1) % 3 === 0 && index + 1 < CODE_LENGTH;
};

interface VerificationCodeFieldProps {
  justify?: string;
  hasError?: boolean;
  onInput: (code: Array<number>) => void;
  onChange: (value: string) => void;
}

export const VerificationCodeField = ({
  justify = 'space-between',
  hasError = false,
  onInput,
  onChange,
}: VerificationCodeFieldProps): JSX.Element => {
  const [valid, setValid] = useState(false);
  const [code, setCode] = useState(() => Array(CODE_LENGTH).fill(''));
  const [refs] = useState(() => code.map(() => createRef<HTMLInputElement>()));

  let shouldFocusPreviousInputOnBackspace = false;

  useEffect(() => {
    // this helps trigger the virtual keyboard in mobile devices
    refs[0]?.current?.setAttribute('autofocus', 'autofocus');
    refs[0]?.current?.focus();
  }, [refs]);

  useEffect(() => {
    onInput(code);
    setValid(isValidCode(code.join('')));
  }, [code]);

  const handleCodePaste = (e: React.ClipboardEvent) => {
    const copiedText = e.clipboardData.getData('text');
    if (copiedText) {
      const pastedCode = copiedText.replace(/\s|-/gim, '');
      if (isValidCode(pastedCode)) {
        e.preventDefault();
        setCode(pastedCode.split(''));
        onChange(pastedCode);
      }
    }
  };

  const handleDigitChange = (e: React.FormEvent<HTMLInputElement>, index: number) => {
    const { value } = e.currentTarget;
    shouldFocusPreviousInputOnBackspace = code[index] && !value;

    if (!value || isValidDigit(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      if (index === newCode.length - 1) {
        const codeStr = newCode.join('');
        if (isValidCode(codeStr)) {
          onChange(newCode.join(''));
        }
      } else if (value) {
        const next = refs[index + 1];
        next?.current?.focus();
      }
    }
  };

  const handleDigitKeyUp = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    const next = refs[index + 1];
    const prev = refs[index - 1];

    if (e.which === BACKSPACE_CODE && prev) {
      if (shouldFocusPreviousInputOnBackspace) prev.current?.focus();
      shouldFocusPreviousInputOnBackspace = true;
    } else if (e.which === LEFT_ARROW_CODE && prev) {
      prev.current?.focus();
    } else if (e.which === RIGHT_ARROW_CODE && next) {
      next?.current?.focus();
    }
  };

  const handleDigitFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  return (
    <StyledWrapper justifyContent={justify}>
      {code.map((digit, index) => (
        <Digit
          value={digit}
          // eslint-disable-next-line react/no-array-index-key
          key={`DigitInput-${index}`}
          valid={valid}
          hasError={hasError}
          ref={refs[index]}
          name={`verification-code-${index}`}
          data-testid={`code-digit-${index}`}
          onFocus={(e) => handleDigitFocus(e)}
          onPaste={(e) => handleCodePaste(e)}
          onKeyUp={(e) => handleDigitKeyUp(e, index)}
          onInput={(e) => handleDigitChange(e, index)}
          shouldRenderSeparator={shouldRenderSeparator(index)}
          autoComplete="one-time-code"
          type="number"
        />
      ))}
    </StyledWrapper>
  );
};
